import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useChat } from "@ai-sdk/react";
import {
  UIMessage,
  DefaultChatTransport,
  lastAssistantMessageIsCompleteWithApprovalResponses,
} from "ai";
import styled from "styled-components";
import ReactTooltip from "react-tooltip";
import { useLocation } from "react-router-dom";
import { FormattedMessage, MessageDescriptor, useIntl } from "react-intl";
import { useProperty } from "../property-provider";
import { useUserProfile } from "../user-profile-provider";
import { IConfig, useConfig } from "../../lib/config";
import {
  BG,
  BORDER,
  BRAND_GRADIENT,
  CONTEXT_ACCENT,
  EXPANDED_PANEL_HEIGHT,
  EXPANDED_PANEL_WIDTH,
  FG,
  GRADIENT,
  PANEL_HEIGHT,
  PANEL_WIDTH,
  SCROLL_THMB,
  TAB_GRADIENT,
} from "./constants";
import { ReactComponent as SparkleIcon } from "./assets/sparkle.svg";
import { ReactComponent as SparkleGradientIcon } from "./assets/sparkle-gradient.svg";
import { ReactComponent as SendCtaIcon } from "./assets/send-cta.svg";
import { ReactComponent as CloseIcon } from "./assets/close.svg";
import { ReactComponent as ExpandIcon } from "./assets/expand.svg";
import { ReactComponent as ContextIcon } from "./assets/context.svg";
import { ReactComponent as MicrophoneIcon } from "./assets/microphone.svg";
import { ReactComponent as HistoryIcon } from "./assets/history.svg";
import { PanelContext, PANEL_CHROME } from "./PanelContext";
import { msgs } from "./msgs";
import { QuickAction, ConversationSummary } from "./types";
import { Messages } from "./components/Messages";
import { WelcomeScreen } from "./components/WelcomeScreen";
import { Loader } from "./components/Loader";
import { ChatHistoryList } from "./components/ChatHistoryList";
import { useLocaleProvider } from "../locale-provider";
import { useAssistantHeaders } from "./hooks/useAssistantHeaders";
import { useConversationId } from "./hooks/useConversationId";
import { useSpeechToText } from "./hooks/useSpeechToText";
import { useChatSessions } from "./hooks/useChatSessions";
import { fetchWithTokenRefresh } from "./fetchWithTokenRefresh";

const Container = styled.div.attrs({ className: "ai-assistant-sidebar" })`
  ::-webkit-scrollbar {
    width: 8px;
  }
  ::-webkit-scrollbar-track {
    background: transparent;
  }
  ::-webkit-scrollbar-thumb {
    background-color: ${SCROLL_THMB};
    border-radius: 4px;
  }
  ::-webkit-scrollbar-thumb:hover {
    background-color: rgba(156, 163, 175, 0.7);
  }
`;

const HeaderTooltip = styled(ReactTooltip)`
  font-family: "Poppins", sans-serif;
  font-size: 11px !important;
  font-weight: 500;
  padding: 6px 10px !important;
  border-radius: 8px !important;
  text-transform: none;
`;

// Collapsed tab: dragged up/down by the user, so its vertical position has to stay a
// runtime style rather than a fixed class.
const TabButton = styled.button<{ $top: number }>`
  background: ${TAB_GRADIENT};
  top: ${p => p.$top}px;
  transform: translateY(-50%);
`;

const TabLabel = styled.span`
  writing-mode: vertical-rl;
  transform: rotate(180deg);
`;

// The panel is always a fixed PANEL_HEIGHT tall (grown to EXPANDED_PANEL_HEIGHT when a
// tall widget like an approval card asks for vertical room instead of width) — including
// on the welcome screen, so starting a conversation from a quick action never resizes
// the panel out from under the user. PANEL_HEIGHT is tuned to fit the quick-actions grid
// without scrolling; see the constant's comment if it needs adjusting.
const panelHeight = (verticalExpand: boolean): string =>
  verticalExpand
    ? `min(${EXPANDED_PANEL_HEIGHT}px, calc(100vh - 2rem))`
    : `min(${PANEL_HEIGHT}px, calc(100vh - 2rem))`;

// Width/height are driven by the expand state (see panelWidth/isVerticalExpand in the
// component), so they stay dynamic props instead of fixed CSS.
const Panel = styled.div<{ $width: number; $verticalExpand: boolean }>`
  width: ${p => p.$width}px;
  max-width: calc(100vw - 2rem);
  height: ${p => panelHeight(p.$verticalExpand)};
  max-height: ${p => panelHeight(p.$verticalExpand)};
  background: ${BG};
  box-shadow: 0px 4px 24px 0px rgba(0, 0, 0, 0.24);
`;

const NewChatButton = styled.button`
  background: ${BRAND_GRADIENT};
`;

const ContextRow = styled.div`
  font-family: "Poppins", sans-serif;
  font-weight: 600;
  font-size: 12px;
  line-height: 1;
`;

const StyledContextIcon = styled(ContextIcon)`
  color: ${CONTEXT_ACCENT};
`;

const ContextChip = styled.span`
  font-family: "Poppins", sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 1;
  color: #4d54d1;
  background: #f1f5ff;
`;

const ComposerGradientBorder = styled.div`
  background: ${GRADIENT};
`;

const ComposerBody = styled.div`
  background: #ffffff;
`;

const MessageTextArea = styled.textarea`
  color: ${FG};
  min-height: 120px;
  max-height: 220px;
  background: #f5f5f5;
  border-style: solid;
  border-color: ${BORDER};
  border-top-width: 1px;
  border-right-width: 1px;
  border-left-width: 1px;
  border-bottom-width: 0;
  border-radius: 12px;
`;

const ChatErrorBanner = styled.div`
  font-family: "Poppins", sans-serif;
  font-size: 12px;
  color: #b91c1c;
  background: #fef2f2;
  border: 1px solid #fecaca;
`;

const ChatErrorRetryButton = styled.button`
  color: #b91c1c;
  text-decoration: underline;
  font-weight: 600;
`;

const Disclaimer = styled.div`
  width: 350px;
  max-width: 100%;
  min-height: 30px;
  color: #949494;
  font-family: "Poppins", sans-serif;
  font-weight: 400;
  font-size: 10px;
  line-height: 100%;
  letter-spacing: 0;
`;

// Only auto-send when every tool call was approved. Denials stay local — the
// denial context will be included naturally in the user's next message.
function sendWhenAllApproved({ messages }: { messages: UIMessage[] }): boolean {
  if (!lastAssistantMessageIsCompleteWithApprovalResponses({ messages })) return false;
  const last = [...messages].reverse().find(m => m.role === "assistant");
  if (!last) return false;
  return last.parts.every(p => {
    const part = p as { type?: string; state?: string; approval?: { approved?: boolean } };
    if (!part.type?.startsWith("tool-")) return true;
    return part.state !== "output-denied" && part.approval?.approved !== false;
  });
}

// Collapsed-tab drag-to-reposition: vertical only, always flush against the
// right edge. Position is remembered across visits so the user doesn't have
// to drag it out of the way again every time it covers something.
const TAB_TOP_STORAGE_KEY = "aiAssistantTabTop";
const TAB_DRAG_THRESHOLD = 4;
const TAB_EDGE_MARGIN = 8;

const HYDRATE_TIMEOUT_MS = 15_000;

// Wraps a handler so it's a no-op while a message is streaming — shared by
// handleSelectSession and handleDeleteSession, since mutating conversation/list
// state mid-turn could corrupt useChat's in-flight streaming state machine.
function guardWhileStreaming<Args extends unknown[]>(
  isStreaming: boolean,
  fn: (...args: Args) => void
): (...args: Args) => void {
  return (...args: Args) => {
    if (isStreaming) return;
    fn(...args);
  };
}

export default function AIAssistantSidebar(): React.JSX.Element | null {
  const intl = useIntl();
  const config: IConfig = useConfig();
  const property = useProperty();
  const userProfile = useUserProfile();
  const userEmail = userProfile?.email || "Y";
  const location = useLocation();
  const { shortLocale, currentLocale } = useLocaleProvider();

  const [conversationId, resetConversationId, loadConversationId] = useConversationId(
    property.ticker
  );
  // "chat" shows the conversation (Messages/WelcomeScreen); "history" shows ChatHistoryList.
  const [view, setView] = useState<"chat" | "history">("chat");

  const [isOpen, setIsOpen] = useState(false);
  // Wide layout. Toggled from the header, and requested automatically by a wide
  // table widget (see PanelContext). Reset when the panel is closed.
  const [isExpanded, setIsExpanded] = useState(false);
  // Which axis the current expansion grows on. "horizontal" (a wide table needs more
  // columns visible) grows panelWidth; "vertical" (an approval card needs more rows
  // visible, e.g. a long change diff) grows panelHeight instead. Null while collapsed.
  const [expandDirection, setExpandDirection] = useState<"horizontal" | "vertical" | null>(null);
  // When a widget asks to expand, it can report the width it needs (a wide table's
  // scrollWidth). We grow the panel to fit it (capped to the viewport via maxWidth)
  // so the whole table is visible without horizontal scroll.
  const [expandTarget, setExpandTarget] = useState<number | null>(null);

  const requestExpand = useCallback((contentWidth?: number) => {
    setIsExpanded(true);
    setExpandDirection("horizontal");
    if (contentWidth && contentWidth > 0) setExpandTarget(contentWidth);
  }, []);

  // "vertical" expansions leave the width alone and grow height instead (see
  // panelHeight below); everything else grows width as before.
  const isVerticalExpand = isExpanded && expandDirection === "vertical";
  // What WidgetTable's own "expand" button cares about: is the panel actually wider
  // than default right now? Not the same as isExpanded, which is also true while
  // vertically expanded (header button) even though the width is back to normal.
  const isWidthExpanded = isExpanded && !isVerticalExpand;

  const panelContextValue = useMemo(() => ({ isWidthExpanded, requestExpand }), [
    isWidthExpanded,
    requestExpand,
  ]);

  const panelWidth =
    !isExpanded || isVerticalExpand
      ? PANEL_WIDTH
      : expandTarget
      ? Math.max(expandTarget + PANEL_CHROME, EXPANDED_PANEL_WIDTH)
      : EXPANDED_PANEL_WIDTH;

  const collapseExpansion = useCallback(() => {
    setIsExpanded(false);
    setExpandTarget(null);
    setExpandDirection(null);
  }, []);

  const closePanel = useCallback(() => {
    setIsOpen(false);
    collapseExpansion();
    setView("chat");
  }, [collapseExpansion]);

  // The header button only ever expands vertically — horizontal room is exclusively
  // requested by the table widget itself (via requestExpand/PanelContext), since a wide
  // table is really the only content that benefits from it. If the table had already
  // expanded the panel horizontally, this switches it back to its original width and
  // grows height instead; clicking again while vertically expanded collapses it.
  const toggleExpand = useCallback(() => {
    if (isVerticalExpand) {
      collapseExpansion();
      return;
    }
    setIsExpanded(true);
    setExpandDirection("vertical");
    setExpandTarget(null);
  }, [isVerticalExpand, collapseExpansion]);

  const tabRef = useRef<HTMLButtonElement | null>(null);
  const tabDragRef = useRef<{
    pointerId: number;
    startClientY: number;
    startTop: number;
    dragging: boolean;
  } | null>(null);
  const tabJustDraggedRef = useRef(false);
  const latestTabTopRef = useRef(0);

  const clampTabTop = useCallback((top: number) => {
    const height = tabRef.current?.offsetHeight ?? 200;
    const min = TAB_EDGE_MARGIN + height / 2;
    const max = window.innerHeight - TAB_EDGE_MARGIN - height / 2;
    return Math.min(Math.max(top, min), Math.max(min, max));
  }, []);

  const [tabTop, setTabTopState] = useState<number>(() => {
    const saved = Number(localStorage.getItem(TAB_TOP_STORAGE_KEY));
    return saved > 0 ? saved : window.innerHeight / 2;
  });
  latestTabTopRef.current = tabTop;

  const setTabTop = useCallback((value: number) => {
    latestTabTopRef.current = value;
    setTabTopState(value);
  }, []);

  useEffect(() => {
    const handleResize = () => setTabTop(clampTabTop(latestTabTopRef.current));
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [clampTabTop, setTabTop]);

  const handleTabPointerDown = useCallback(
    (e: React.PointerEvent<HTMLButtonElement>) => {
      tabDragRef.current = {
        pointerId: e.pointerId,
        startClientY: e.clientY,
        startTop: tabTop,
        dragging: false,
      };
      e.currentTarget.setPointerCapture(e.pointerId);
    },
    [tabTop]
  );

  const handleTabPointerMove = useCallback(
    (e: React.PointerEvent<HTMLButtonElement>) => {
      const drag = tabDragRef.current;
      if (!drag || drag.pointerId !== e.pointerId) return;
      const delta = e.clientY - drag.startClientY;
      if (!drag.dragging && Math.abs(delta) < TAB_DRAG_THRESHOLD) return;
      drag.dragging = true;
      setTabTop(clampTabTop(drag.startTop + delta));
    },
    [clampTabTop, setTabTop]
  );

  const handleTabPointerUp = useCallback((e: React.PointerEvent<HTMLButtonElement>) => {
    const drag = tabDragRef.current;
    if (!drag || drag.pointerId !== e.pointerId) return;
    if (drag.dragging) {
      tabJustDraggedRef.current = true;
      localStorage.setItem(TAB_TOP_STORAGE_KEY, String(latestTabTopRef.current));
    }
    tabDragRef.current = null;
  }, []);

  const handleTabOpen = useCallback(() => {
    if (tabJustDraggedRef.current) {
      tabJustDraggedRef.current = false;
      return;
    }
    setIsOpen(true);
  }, []);

  const [input, setInput] = useState("");
  const [contextEnabled, setContextEnabled] = useState(true);

  const section = location.pathname.includes("promotions") ? "promotions" : "none";

  useEffect(() => {
    setContextEnabled(true);
  }, [location.pathname]);

  // The context chip shows only the current module (e.g. "Promociones"), never the
  // specific entity id — this chat is reused across sections and the context always
  // refers to a single module.
  const contextLabel = useMemo(() => intl.formatMessage(msgs.contextPromotions), [intl]);

  const buildHeaders = useAssistantHeaders({
    property,
    userProfile,
    contextEnabled,
    section,
    shortLocale,
    conversationId,
  });

  const transport = useMemo(
    () =>
      new DefaultChatTransport({
        api: `${config.backofficeAssistantApiEndpoint}/chat`,
        headers: buildHeaders,
        // A 401 mid-conversation (the RPT aged out) is refreshed and retried ONCE,
        // transparently — see fetchWithTokenRefresh. property.refreshToken is stable across
        // re-renders (only changes if the property itself changes), so this never forces an
        // unnecessary transport recreation.
        //
        // If the refresh itself fails (the underlying Keycloak session is gone too, not just the
        // derived RPT), there is no token left to retry with — the only way out is a full reload,
        // which re-runs the property/token bootstrap from scratch. This is deliberately silent
        // (no "your session expired" message): the user should never have to know a session
        // expired at all, same as how useHttpClient/http.ts already handles this elsewhere in the
        // app. Whatever the user typed is lost, but that's unavoidable — there is no valid
        // credential left to resubmit it with.
        fetch: (input, init) =>
          fetchWithTokenRefresh(input, init, property.refreshToken, undefined, () =>
            window.location.reload()
          ),
      }),
    [config.backofficeAssistantApiEndpoint, buildHeaders, property.refreshToken]
  );

  const {
    messages,
    sendMessage,
    status,
    regenerate,
    addToolApprovalResponse,
    setMessages,
  } = useChat({
    messages: [],
    sendAutomaticallyWhen: sendWhenAllApproved,
    transport,
    onError: err => {
      // The banner below (status === "error") is the actual user-facing surface; this is just
      // so a failed turn leaves a trace in the console for debugging instead of vanishing.
      console.error("[AIAssistant] Chat request failed", err);
    },
  });

  const [hydration, setHydration] = useState<"idle" | "loading" | "done">("idle");
  const hydratedRef = useRef(false);
  // Ids of the messages that came from stored history (a previous session). Used to mark a
  // never-answered approval in one of them as non-actionable — approving a stale turn can't
  // execute the original tool call and leaves a dangling function call (see WidgetApproval).
  const [historicalIds, setHistoricalIds] = useState<Set<string>>(() => new Set());

  // Tracks the last property.ticker seen by the reset effect below, distinct from
  // hydratedRef (which tracks hydration completion). Initialized to null (never a real
  // ticker value) so the effect's first run can be told apart from an actual switch.
  const previousTickerRef = useRef<string | null>(null);

  // Fetches history for an explicit session id (mount-time hydration or a row selected
  // from the history panel) — owns its own timeout and supersedes any prior in-flight
  // call, so a stale response can never overwrite a newer selection.
  const hydrateAbortControllerRef = useRef<AbortController | null>(null);
  const hydrate = useCallback(
    (id: string) => {
      hydrateAbortControllerRef.current?.abort();
      const controller = new AbortController();
      hydrateAbortControllerRef.current = controller;
      let timedOut = false;
      const timeoutId = setTimeout(() => {
        timedOut = true;
        controller.abort();
      }, HYDRATE_TIMEOUT_MS);

      return fetchWithTokenRefresh(
        `${config.backofficeAssistantApiEndpoint}/chat/history`,
        {
          method: "GET",
          // Overrides buildHeaders()'s session id with the id we were explicitly asked to
          // hydrate: loadConversationId/resetConversationId update conversationId state just
          // before calling hydrate(id), but that state update isn't visible to
          // useAssistantHeaders's ref until the next render — so buildHeaders() alone would
          // still report the PREVIOUS session here and fetch the wrong conversation's history.
          headers: { ...buildHeaders(), "x-chat-session-id": id },
          signal: controller.signal,
        },
        property.refreshToken,
        undefined,
        () => window.location.reload()
      )
        .then(r => (r.ok ? r.json() : Promise.resolve({ messages: [] })))
        .then((data: { messages: UIMessage[] }) => {
          const loaded = data.messages ?? [];
          setMessages(loaded);
          setHistoricalIds(new Set(loaded.map(m => m.id)));
        })
        .catch(err => {
          if (controller.signal.aborted && !timedOut) return;
          console.error("[AIAssistant] Failed to load chat history", { id }, err);
        })
        .finally(() => {
          clearTimeout(timeoutId);
          if (hydrateAbortControllerRef.current === controller) {
            hydrateAbortControllerRef.current = null;
          }
          setHydration("done");
        });
    },
    [config.backofficeAssistantApiEndpoint, buildHeaders, setMessages, property.refreshToken]
  );

  // Detects an actual property switch (not the initial mount) and resets the sidebar's
  // in-memory state so a previous property's messages never bleed into the new one.
  useEffect(() => {
    const previous = previousTickerRef.current;
    previousTickerRef.current = property.ticker;
    if (previous === null || previous === property.ticker) return;
    console.debug("[AIAssistant] Property switch detected, starting a blank session", {
      from: previous,
      to: property.ticker,
    });
    resetConversationId();
    setMessages([]);
    setHistoricalIds(new Set());
    hydratedRef.current = false;
  }, [property.ticker, resetConversationId, setMessages]);

  useEffect(() => {
    const ready =
      isOpen &&
      section === "promotions" &&
      !!property.token &&
      !!property.ticker &&
      !!conversationId &&
      conversationId !== "none";

    if (!ready || hydratedRef.current) return;

    hydratedRef.current = true;
    setHydration("loading");
    void hydrate(conversationId);
  }, [isOpen, section, property.token, property.ticker, conversationId, hydrate]);

  // If an action is awaiting approval (the Yes/No buttons) and the user types a new
  // message instead of clicking one, treat it as "changed my mind": deny every pending
  // approval first. Beyond the UX, this is REQUIRED — an unresolved approval leaves a
  // dangling tool call (a functionCall with no functionResponse) and the backend request
  // to Gemini fails silently, so the chat would appear not to answer. Denials stay local
  // (sendWhenAllApproved won't auto-send on a denial), so we then send the message
  // ourselves and the denial context travels naturally with it.
  const denyPendingApprovals = useCallback(() => {
    for (const m of messages) {
      if (m.role !== "assistant") continue;
      for (const p of m.parts) {
        const part = p as {
          type?: string;
          state?: string;
          approval?: { id?: string; approved?: boolean };
        };
        if (
          part.type?.startsWith("tool-") &&
          part.state === "approval-requested" &&
          part.approval?.id &&
          part.approval.approved === undefined
        ) {
          addToolApprovalResponse({ id: part.approval.id, approved: false });
        }
      }
    }
  }, [messages, addToolApprovalResponse]);

  const submitText = useCallback(
    (text: string) => {
      const trimmed = text.trim();
      if (!trimmed) return;
      denyPendingApprovals();
      void sendMessage({ text: trimmed });
      setInput("");
    },
    [denyPendingApprovals, sendMessage]
  );

  const handleRetry = useCallback(() => {
    void regenerate();
  }, [regenerate]);

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      submitText(input);
    },
    [submitText, input]
  );

  const handleQuickAction = useCallback(
    (action: QuickAction) => {
      submitText(intl.formatMessage(action.prompt));
    },
    [submitText, intl]
  );

  const handleApproval = useCallback(
    (id: string, approved: boolean) => {
      addToolApprovalResponse({ id, approved });
    },
    [addToolApprovalResponse]
  );

  const hasConversation = messages.length > 0;
  const isStreaming = status === "submitted" || status === "streaming";

  const handleNewConversation = useCallback(() => {
    resetConversationId();
    setMessages([]);
    setHistoricalIds(new Set());
    // A freshly generated conversation id is guaranteed to have no server-side history,
    // so we mark it as already hydrated instead of letting the mount effect re-fetch —
    // that network round trip was the cause of a brief Loader (AI avatar + dots) flash
    // between the history/new-chat action and the welcome screen showing up.
    hydratedRef.current = true;
    setHydration("done");
    setView("chat");
  }, [resetConversationId, setMessages]);

  const {
    sessions,
    loading: sessionsLoading,
    error: sessionsError,
    listSessions,
    deleteSession,
  } = useChatSessions({
    endpoint: config.backofficeAssistantApiEndpoint,
    buildHeaders,
    refreshToken: property.refreshToken,
  });

  // Local mirror of the hook's sessions, so an optimistic delete can splice a row out
  // immediately without waiting for a listSessions() round trip. Re-synced to the
  // hook's own state every time listSessions() resolves (i.e. every time history opens).
  const [historySessions, setHistorySessions] = useState<ConversationSummary[]>([]);
  useEffect(() => {
    setHistorySessions(sessions);
  }, [sessions]);

  const handleToggleHistory = useCallback(() => {
    setView(v => {
      const next = v === "history" ? "chat" : "history";
      if (next === "history") listSessions();
      return next;
    });
  }, [listSessions]);

  const handleSelectSession = useCallback(
    (id: string) => {
      guardWhileStreaming(isStreaming, (sessionId: string) => {
        loadConversationId(sessionId);
        setHydration("loading");
        void hydrate(sessionId);
        setView("chat");
      })(id);
    },
    [isStreaming, loadConversationId, hydrate]
  );

  const handleDeleteSession = useCallback(
    (id: string) => {
      guardWhileStreaming(isStreaming, (sessionId: string) => {
        const removed = historySessions.find(s => s.id === sessionId);
        setHistorySessions(prev => prev.filter(s => s.id !== sessionId));

        if (sessionId === conversationId) {
          resetConversationId();
          setMessages([]);
          setHistoricalIds(new Set());
          // Same reasoning as handleNewConversation: the replacement id is brand new,
          // so skip the mount effect's re-fetch and its Loader flash.
          hydratedRef.current = true;
          setHydration("done");
        }

        deleteSession(sessionId).catch(err => {
          console.error("[AIAssistant] Failed to delete chat session", err);
          if (removed) {
            setHistorySessions(prev =>
              prev.some(s => s.id === removed.id)
                ? prev
                : [...prev, removed].sort(
                    (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
                  )
            );
          }
        });
      })(id);
    },
    [isStreaming, historySessions, conversationId, resetConversationId, setMessages, deleteSession]
  );

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value);
  }, []);

  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  // Auto-grow the textarea up to 200px so short messages don't reserve the full
  // max-height, while long ones still scroll instead of pushing the panel taller.
  const adjustTextareaHeight = useCallback(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = "auto";
    ta.style.height = `${Math.max(120, Math.min(ta.scrollHeight, 200))}px`;
  }, []);

  // Runs on typing (onInput) but also whenever `input` changes programmatically — e.g.
  // dictated text appended by the microphone button — so the box grows either way.
  useEffect(() => {
    adjustTextareaHeight();
  }, [input, adjustTextareaHeight]);

  const handleTextareaKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        submitText(input);
      }
    },
    [submitText, input]
  );

  const handleRemoveContext = useCallback(() => setContextEnabled(false), []);

  const [voiceError, setVoiceError] = useState<string | null>(null);

  // Appends each dictated chunk to whatever is already typed, adding a separating space
  // only when needed, so voice input can be mixed freely with manual typing.
  const handleVoiceResult = useCallback((transcript: string) => {
    setVoiceError(null);
    setInput(prev => {
      const base = prev.trimEnd();
      return base ? `${base} ${transcript.trim()}` : transcript.trim();
    });
  }, []);

  const handleVoiceError = useCallback(
    (error: string) => {
      // Surface the raw error code in the console — the UI message is deliberately generic
      // per error type, but this is what actually distinguishes "mic blocked" from "no
      // network" etc. when debugging a report.
      console.error("[AIAssistant] Speech-to-text error:", error);
      const messageByError: Record<string, MessageDescriptor> = {
        "not-allowed": msgs.voiceErrorNotAllowed,
        "no-mic": msgs.voiceErrorNoMic,
        network: msgs.voiceErrorNetwork,
        "transcription-failed": msgs.voiceErrorTranscriptionFailed,
      };
      setVoiceError(intl.formatMessage(messageByError[error] ?? msgs.voiceError));
    },
    [intl]
  );

  const {
    isSupported: isVoiceSupported,
    isListening: isVoiceListening,
    isTranscribing: isVoiceTranscribing,
    toggle: toggleVoiceInput,
  } = useSpeechToText({
    lang: currentLocale,
    endpoint: `${config.backofficeAssistantApiEndpoint}/speech-to-text`,
    buildHeaders,
    refreshToken: property.refreshToken,
    onFinalResult: handleVoiceResult,
    onError: handleVoiceError,
  });

  const handleMicClick = useCallback(() => {
    setVoiceError(null);
    toggleVoiceInput();
  }, [toggleVoiceInput]);

  // Don't let dictation keep running in the background once the panel is closed.
  useEffect(() => {
    if (!isOpen && isVoiceListening) toggleVoiceInput();
  }, [isOpen, isVoiceListening, toggleVoiceInput]);

  if (section !== "promotions") {
    return null;
  }

  return (
    <Container>
      {!isOpen && (
        <TabButton
          ref={tabRef}
          type="button"
          $top={tabTop}
          onClick={handleTabOpen}
          onPointerDown={handleTabPointerDown}
          onPointerMove={handleTabPointerMove}
          onPointerUp={handleTabPointerUp}
          onPointerCancel={handleTabPointerUp}
          aria-label={intl.formatMessage(msgs.tabLabel)}
          className="fixed right-0 z-[60] flex flex-col items-center gap-[6px] rounded-l py-6 px-3 text-white shadow-lg cursor-grab touch-none focus:outline-none active:cursor-grabbing">
          <em className="fa fa-chevron-left text-xs" />
          <TabLabel className="text-sm font-bold tracking-wide">
            <FormattedMessage {...msgs.tabLabel} />
          </TabLabel>
          <SparkleIcon className="w-4 h-4" aria-hidden />
        </TabButton>
      )}

      {isOpen && (
        <PanelContext.Provider value={panelContextValue}>
          {isExpanded && (
            <div
              onClick={collapseExpansion}
              aria-hidden
              className="fixed inset-0 z-[59] bg-black/40 transition-opacity duration-200"
            />
          )}
          <Panel
            className="fixed right-0 top-1/2 -translate-y-1/2 z-[60] flex flex-col gap-4 rounded-l-3xl p-6 transition-all duration-200 ease-out"
            $width={panelWidth}
            $verticalExpand={isVerticalExpand}>
            <div className="flex items-center justify-between flex-shrink-0 h-8">
              <span className="flex items-center">
                {(hasConversation || view === "history") && (
                  <SparkleGradientIcon className="w-6 h-6" aria-hidden />
                )}
              </span>
              <div className="flex items-center gap-2">
                {(hasConversation || view === "history") && (
                  <NewChatButton
                    type="button"
                    onClick={handleNewConversation}
                    disabled={isStreaming}
                    aria-label={intl.formatMessage(msgs.buttonNewConversation)}
                    className="flex items-center px-3 py-1 rounded-full text-white text-xs font-medium disabled:opacity-40 disabled:cursor-not-allowed transition-opacity focus:outline-none">
                    <FormattedMessage {...msgs.buttonNewConversation} />
                  </NewChatButton>
                )}
                <button
                  type="button"
                  onClick={handleToggleHistory}
                  aria-label={intl.formatMessage(msgs.buttonHistory)}
                  data-tip="React-tooltip"
                  data-for="ai-assistant-history-tooltip"
                  className="opacity-60 hover:opacity-100 transition-colors focus:outline-none">
                  <HistoryIcon className="w-5 h-5" />
                </button>
                <button
                  type="button"
                  onClick={toggleExpand}
                  aria-label={intl.formatMessage(
                    isVerticalExpand ? msgs.buttonCollapse : msgs.buttonExpand
                  )}
                  data-tip="React-tooltip"
                  data-for="ai-assistant-expand-tooltip"
                  className="opacity-60 hover:opacity-100 transition-colors focus:outline-none">
                  <ExpandIcon className="w-5 h-5" />
                </button>
                <button
                  type="button"
                  onClick={closePanel}
                  aria-label={intl.formatMessage(msgs.buttonClose)}
                  data-tip="React-tooltip"
                  data-for="ai-assistant-close-tooltip"
                  className="opacity-60 hover:opacity-100 transition-colors focus:outline-none">
                  <CloseIcon className="w-5 h-5" />
                </button>
                <HeaderTooltip id="ai-assistant-history-tooltip" effect="solid" place="bottom">
                  <FormattedMessage {...msgs.tooltipHistory} />
                </HeaderTooltip>
                <HeaderTooltip id="ai-assistant-expand-tooltip" effect="solid" place="bottom">
                  <FormattedMessage {...msgs.tooltipExpand} />
                </HeaderTooltip>
                <HeaderTooltip id="ai-assistant-close-tooltip" effect="solid" place="bottom">
                  <FormattedMessage {...msgs.tooltipClose} />
                </HeaderTooltip>
              </div>
            </div>

            {view === "history" ? (
              <ChatHistoryList
                sessions={historySessions}
                loading={sessionsLoading}
                error={sessionsError}
                disabled={isStreaming}
                onSelect={handleSelectSession}
                onDelete={handleDeleteSession}
              />
            ) : hydration === "loading" ? (
              <Loader />
            ) : hasConversation ? (
              <Messages
                messages={messages}
                status={status}
                userEmail={userEmail}
                propertyTicker={property.ticker}
                onApproval={handleApproval}
                historicalIds={historicalIds}
              />
            ) : (
              <WelcomeScreen onAction={handleQuickAction} />
            )}

            {view === "chat" && contextEnabled && (
              <ContextRow className="flex flex-shrink-0 items-center gap-2 px-1 text-[#6B7280]">
                <StyledContextIcon className="w-4 h-4" aria-hidden />
                <span>
                  <FormattedMessage {...msgs.contextLabel} />
                </span>
                <ContextChip className="inline-flex items-center gap-1 rounded-full px-2 py-0.5">
                  {contextLabel}
                  <button
                    type="button"
                    onClick={handleRemoveContext}
                    aria-label={intl.formatMessage(msgs.buttonRemoveContext)}
                    className="opacity-70 hover:opacity-100">
                    <em className="fa fa-times text-[10px]" />
                  </button>
                </ContextChip>
              </ContextRow>
            )}

            {view === "chat" && status === "error" && (
              // Deliberately generic — never mentions the token/session, since a 401 whose
              // refresh-and-retry (fetchWithTokenRefresh) succeeded never reaches this at all,
              // and a refresh that fails outright triggers a silent reload instead (see the
              // transport's fetch above), not this banner. So whatever lands here is some other,
              // unrelated failure (network blip, backend error, etc.).
              <ChatErrorBanner className="flex flex-shrink-0 items-center justify-between gap-2 rounded-xl px-3 py-2">
                <span>
                  <FormattedMessage {...msgs.chatErrorMessage} />
                </span>
                <ChatErrorRetryButton type="button" onClick={handleRetry}>
                  <FormattedMessage {...msgs.chatErrorRetry} />
                </ChatErrorRetryButton>
              </ChatErrorBanner>
            )}

            {view === "chat" && (
              <div className="flex-shrink-0">
                <form onSubmit={handleSubmit}>
                  <ComposerGradientBorder className="rounded-[24px] p-px">
                    <ComposerBody className="rounded-[23px] p-3 flex flex-col gap-[10px]">
                      <MessageTextArea
                        ref={textareaRef}
                        value={input}
                        onChange={handleInputChange}
                        placeholder={intl.formatMessage(msgs.inputPlaceholder)}
                        className="w-full px-3.5 py-2.5 text-sm resize-none overflow-y-auto focus:outline-none"
                        rows={5}
                        onKeyDown={handleTextareaKeyDown}
                      />

                      {voiceError && (
                        <span className="px-1 text-[11px] text-red-500">{voiceError}</span>
                      )}

                      <div className="flex items-center justify-end px-1">
                        <div className="flex items-center gap-1">
                          {isVoiceSupported && (
                            <button
                              type="button"
                              onClick={handleMicClick}
                              disabled={isVoiceTranscribing}
                              aria-pressed={isVoiceListening}
                              aria-busy={isVoiceTranscribing}
                              aria-label={intl.formatMessage(
                                isVoiceTranscribing
                                  ? msgs.voiceTranscribing
                                  : isVoiceListening
                                  ? msgs.buttonStopVoice
                                  : msgs.buttonStartVoice
                              )}
                              title={intl.formatMessage(
                                isVoiceTranscribing
                                  ? msgs.voiceTranscribing
                                  : isVoiceListening
                                  ? msgs.buttonStopVoice
                                  : msgs.buttonStartVoice
                              )}
                              className={`flex items-center justify-center rounded-full transition-colors focus:outline-none disabled:opacity-40 disabled:cursor-not-allowed ${
                                isVoiceListening
                                  ? "text-red-500 animate-pulse"
                                  : "text-black opacity-70 hover:opacity-100"
                              }`}>
                              <MicrophoneIcon
                                className={`w-[29px] h-[29px] ${
                                  isVoiceTranscribing ? "animate-pulse" : ""
                                }`}
                              />
                            </button>
                          )}
                          <button
                            type="submit"
                            disabled={!input.trim()}
                            aria-label={intl.formatMessage(msgs.buttonSend)}
                            className="flex items-center justify-center rounded-full disabled:opacity-40 transition-opacity focus:outline-none">
                            <SendCtaIcon className="w-9 h-9" />
                          </button>
                        </div>
                      </div>
                    </ComposerBody>
                  </ComposerGradientBorder>
                </form>
              </div>
            )}

            <Disclaimer className="flex flex-shrink-0 items-center justify-center mx-auto mt-2 px-2 text-center">
              <FormattedMessage {...msgs.disclaimer} />
            </Disclaimer>
          </Panel>
        </PanelContext.Provider>
      )}
    </Container>
  );
}
